import React, { useState } from 'react';

/** One label/value line in the booking recap. */
export interface CheckoutRecapRow {
  label: string;
  value: string;
}

export interface CheckoutRecapProps {
  /** Booking facts — restaurant, date, time, guests, experience… */
  rows: CheckoutRecapRow[];
  /** Money breakdown — deposit, admin fee. Rendered with tabular numerals. */
  moneyRows?: CheckoutRecapRow[];
  totalLabel?: string;
  /** Pre-formatted total, e.g. `€101.50`. */
  totalValue: string;
  /** `inline` collapses behind a summary bar (widget); `page` is always open (hosted page). */
  variant?: 'inline' | 'page';
  /** Collapsed summary line — falls back to the first two rows' values. */
  summaryTitle?: string;
  summarySubtitle?: string;
  defaultOpen?: boolean;
  labels?: Partial<{ show: string; hide: string }>;
}

/**
 * Booking + money recap shared by the inline widget checkout and the hosted checkout page.
 * Pure presentation: every value arrives pre-formatted so the money maths stays in one place
 * (apps/widget/src/logic/depositCalculation.ts server-side equivalents).
 */
const CheckoutRecap: React.FC<CheckoutRecapProps> = ({
  rows,
  moneyRows = [],
  totalLabel = 'Total due now',
  totalValue,
  variant = 'inline',
  summaryTitle,
  summarySubtitle,
  defaultOpen = true,
  labels,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  const body = (
    <>
      {rows.map((row) => (
        <div className="ck-recap-row" key={`r-${row.label}`}>
          <span className="ck-recap-row-label">{row.label}</span>
          <span className="ck-recap-row-value">{row.value}</span>
        </div>
      ))}

      {moneyRows.length > 0 && <hr className="ck-recap-rule" />}

      {moneyRows.map((row) => (
        <div className="ck-recap-row ck-recap-row--money" key={`m-${row.label}`}>
          <span className="ck-recap-row-label">{row.label}</span>
          <span className="ck-recap-row-value">{row.value}</span>
        </div>
      ))}

      <div className="ck-recap-total">
        <span className="ck-recap-total-label">{totalLabel}</span>
        <span className="ck-recap-total-value">{totalValue}</span>
      </div>
    </>
  );

  if (variant === 'page') {
    return <div className="ck-page-recap-card">{body}</div>;
  }

  const title = summaryTitle || rows[0]?.value || totalLabel;
  const subtitle = summarySubtitle || rows[1]?.value || '';

  return (
    <div className="ck-recap">
      <button
        type="button"
        className="ck-recap-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="ck-recap-toggle-main">
          <span className="ck-recap-toggle-title">{title}</span>
          {subtitle ? <span className="ck-recap-toggle-sub">{subtitle}</span> : null}
        </span>
        <span className="ck-recap-toggle-action">
          {open ? labels?.hide || 'Hide details' : labels?.show || 'Show details'}
        </span>
      </button>
      {open && <div className="ck-recap-body">{body}</div>}
    </div>
  );
};

export default CheckoutRecap;
